<template>
  <div class="flex h-full flex-col">
    <!-- モバイル用ヘッダー -->
    <div class="flex items-center justify-between border-b bg-white p-3 md:hidden">
      <h3 class="text-lg font-semibold text-gray-800">{{ $t('stageManager.title') }}</h3>
      <button
        class="rounded-lg p-2 text-gray-500 transition-all duration-150 ease-out hover:bg-gray-100 hover:text-gray-700"
        @click="closeMobilePanel"
      >
        <span
          class="material-symbols-outlined notranslate text-xl"
          translate="no"
          aria-hidden="true"
          >close</span
        >
      </button>
    </div>

    <div ref="scrollArea" class="relative grow overflow-y-auto">
      <div v-if="!isSending">
        <div v-for="[stackPath, card] in stageCardEntity" :key="stackPath">
          <StageCard
            :card="card"
            @stage-card-edit="() => handleStageCardEdit(stackPath)"
            @stage-card-delete="handleStageCardDelete(stackPath, card)"
          />
        </div>
        <div
          class="mr-3 mt-3 text-right text-support"
          :disabled="blocking"
          @click="handleOpenAllStageDeleteModal"
        >
          {{ $t('stageManager.discardAll') }}
        </div>
      </div>
      <div v-else class="flex size-full items-center justify-center">
        <div
          className="animate-spin h-10 w-10 border-4 border-blue-500 rounded-full border-t-transparent"
        ></div>
      </div>
    </div>

    <!-- Message & Submission Area -->
    <div class="border-t bg-gray-100 p-4 shadow-md">
      <MessageNotification
        v-if="errorMessage !== ''"
        variant="custom"
        :icon="'error'"
        custom-classes="mb-2 text-red-500"
        :message="errorMessage"
      />
      <div class="text-gray-700">
        {{ $t('stageManager.message.notSaved') }}<br />
        {{ $t('stageManager.message.saveInstruction') }}
      </div>
      <div class="mt-4 flex flex-col">
        <textarea
          id="inline-client-name"
          v-model="commitMessage"
          class="input-text-area h-36"
          type="text"
          name="name"
          :placeholder="$t('stageManager.placeholder')"
        ></textarea>
        <button class="button-blue mt-4 w-full" :disabled="blocking" @click="submit">
          <template v-if="!blocking">{{ $t('stageManager.save') }}</template>
          <template v-else>{{ $t('stageManager.processing') }}</template>
        </button>
      </div>
    </div>
  </div>
  <AllStageDeleteModal
    :is-visible="isVisibleAllStageDeleteModal"
    :stage-card-entity="stageCardEntity"
    @close="handleCloseAllStageDeleteModal"
  />
</template>

<script setup lang="ts">
import { useStageStore } from '@/stores/stage'
import { useStack, type Page } from 'vue-stacked-ui'
import { commit } from '../lib/mutation'
import { ref, watch } from 'vue'
import { useTranslation } from 'i18next-vue'
import StageCard from './StageManager/StageCard.vue'
import { updateStageCardEntity } from '@/lib/stageCardUtil'
import type { stageCard } from '@/lib/stageCardUtil'
import * as fastq from 'fastq'
import AllStageDeleteModal from './StageManager/AllStageDeleteModal.vue'
import MessageNotification from '@/components/MessageNotification.vue'

const { t } = useTranslation()
const store = useStageStore()
const stage = store.getAll()

const stageCardEntity = ref(new Map<string, stageCard>())
const stack = useStack()

const handleStageCardEdit = (stackPath: string) => {
  const page = stack.getStacks().find((page: Page) => page.route.path === stackPath)
  if (page !== undefined) {
    stack.remove(page)
  }
  stack.push(stackPath)
}

const handleStageCardDelete = (stackPath: string, card: stageCard) => {
  card.stageKeys.forEach((stageKey) => {
    store.reset(stageKey)
  })
}

const commitMessage = ref('')
const errorMessage = ref('')
const isSending = ref(false)

const emit = defineEmits<{
  (e: 'update:isSending', value: boolean): void
  (e: 'close-mobile-panel'): void
}>()

// モバイルパネルを閉じる
const closeMobilePanel = () => {
  emit('close-mobile-panel')
}

const submit = async () => {
  errorMessage.value = ''
  console.log(stage)
  const params = [...Array.from(stage.values())]
  const backup = store.backupStage()
  updateIsSending(true)
  store.clear()
  try {
    await commit(params, commitMessage.value)
    updateIsSending(false)
    commitMessage.value = ''
    closeMobilePanel()
  } catch (e) {
    console.error(e)
    store.restoreStage(backup)
    updateIsSending(false)
    errorMessage.value = t('stageManager.error')
  }
}

function updateIsSending(value: boolean) {
  isSending.value = value
  emit('update:isSending', isSending.value)
}

const blocking = ref(false)
const queue = fastq.promise(async (fn) => fn(), 1)
const enqueue = (fn: () => Promise<void>) => {
  queue.kill()
  queue.drain = async () => {
    // wait for no other queues
    await new Promise((r) => setTimeout(r, 300))
    blocking.value = false
  }
  blocking.value = true
  // minimize calls to updateStageCardEntity
  queue.push(async () => await new Promise((r) => setTimeout(r, 100)))
  queue.push(fn)
}

const isVisibleAllStageDeleteModal = ref(false)
const handleOpenAllStageDeleteModal = () => {
  isVisibleAllStageDeleteModal.value = true
}
const handleCloseAllStageDeleteModal = () => (isVisibleAllStageDeleteModal.value = false)

watch(
  [stage],
  async () => {
    enqueue(async () => {
      await updateStageCardEntity(stage, stageCardEntity.value)
    })
  },
  { immediate: true }
)
</script>
